import React from 'react';
import { Shield, Lock, Eye, Database, Clock, Trash2, Download, UserCheck, Flag } from 'lucide-react';

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-8 sm:px-8">
            <h1 className="text-3xl font-bold text-white">
              Politique de Confidentialité
            </h1>
            <p className="mt-2 text-teal-100">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-8 sm:px-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <Shield className="h-6 w-6 text-teal-600" />
                Protection de vos Données
              </h2>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700">
                  Emploi Paysagiste, entité du groupe Recrutop, s'engage à protéger la confidentialité de vos données personnelles. 
                  Cette politique détaille nos pratiques concernant la collecte, l'utilisation et la protection de vos informations.
                </p>
              </div>
            </section>

            {/* Données Collectées */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <Database className="h-6 w-6 text-teal-600" />
                Données Collectées
              </h2>
              <div className="bg-gray-50 rounded-xl p-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <UserCheck className="h-5 w-5 text-teal-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Informations Personnelles</h3>
                      <p className="text-gray-700">Nom, prénom, email, numéro de téléphone</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Download className="h-5 w-5 text-teal-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Documents</h3>
                      <p className="text-gray-700">CV, lettres de motivation</p>
                    </div>
                  </li>
                </ul>
              </div>
            </section>

            {/* Utilisation des Données */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <Eye className="h-6 w-6 text-teal-600" />
                Utilisation des Données
              </h2>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 mb-4">
                  Vos données sont utilisées pour :
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-3 text-gray-700">
                    <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                    Faciliter votre recherche d'emploi
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                    Vous mettre en relation avec des recruteurs
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                    Améliorer nos services
                  </li>
                </ul>
              </div>
            </section>

            {/* Conservation des Données */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <Clock className="h-6 w-6 text-teal-600" />
                Conservation des Données
              </h2>
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div>
                    <p className="text-gray-700 mb-4">
                      Vos données personnelles sont conservées pendant une durée de 2 ans à compter de votre dernière activité sur notre plateforme.
                    </p>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Flag className="h-5 w-5 text-blue-600" />
                      <span>Hébergement sécurisé en France (OVH)</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Vos Droits */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <Lock className="h-6 w-6 text-teal-600" />
                Vos Droits
              </h2>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 mb-4">
                  Conformément à la réglementation, vous disposez des droits suivants :
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-3 text-gray-700">
                    <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                    Accès à vos données personnelles
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                    Rectification de vos données
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                    Suppression de vos données
                  </li>
                </ul>
              </div>
            </section>

            {/* Suppression des Données */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <Trash2 className="h-6 w-6 text-teal-600" />
                Suppression des Données
              </h2>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700">
                  Pour toute demande de suppression de vos données, contactez-nous à :{' '}
                  <a 
                    href="mailto:contact@emploi-paysagiste.fr"
                    className="text-teal-600 hover:text-teal-700 font-medium"
                  >
                    contact@emploi-paysagiste.fr
                  </a>
                </p>
              </div>
            </section>

            {/* Note de bas de page */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                Cette politique de confidentialité peut être modifiée à tout moment.
                Les utilisateurs seront informés des changements via notre plateforme.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}